import { useMemo } from "react";
import { todayKey, dateKey } from "../utils/helpers";
import { getDailyQuestSummary } from "../utils/daily-quests";
import { MACRO_GOALS } from "../data/meals";
import "../styles/home-surface.css";

/**
 * The supporting detail — what else happened today.
 *
 * This used to be the headline of the home screen: a score out of 100 with
 * calories at the top of it. In a mental-health product that is both a tonal
 * mismatch and a liability, so it now sits last and quiet. The numbers are all
 * still here; none of them is allowed to be the biggest thing on the screen,
 * and nothing here is framed as a pass or a fail.
 */
export default function DailyScoreCard({
  workouts,
  runs,
  mealLog,
  todayTotals,
  macroGoals,
  weightLog,
  onNavigateToNutrition,
  onStartWorkout,
}) {
  const today = todayKey();
  const goals = macroGoals || MACRO_GOALS;

  const score = useMemo(() => {
    const didWorkout = (workouts || []).some(
      (w) => dateKey(w.startTime) === today
    );
    const didRun = (runs || []).some((r) => dateKey(r.timestamp) === today);
    const movementPts = didWorkout ? 45 : didRun ? 35 : 0;

    const proteinGoal = goals?.protein || 0;
    const proteinToday = todayTotals?.protein || 0;
    const proteinPts =
      proteinGoal > 0
        ? Math.min(35, Math.round((proteinToday / proteinGoal) * 35))
        : 0;

    const { completed: questsDone } = getDailyQuestSummary({
      workouts: workouts || [],
      runs: runs || [],
      mealLog: mealLog || {},
      todayTotals: todayTotals || {},
      macroGoals: goals || {},
      weightLog: weightLog || [],
    });
    const questPts = Math.round((questsDone / 3) * 20);

    const total = movementPts + proteinPts + questPts;

    return {
      total,
      movementPts,
      proteinPts,
      questPts,
      didWorkout,
      didRun,
      questsDone,
    };
  }, [workouts, runs, mealLog, todayTotals, goals, weightLog, today]);

  // ── Macro progress. Protein only on this surface. ──
  const cals = todayTotals?.calories || 0;
  const protein = todayTotals?.protein || 0;
  const proGoal = goals.protein || 150;
  const proPct = Math.min(100, Math.round((protein / proGoal) * 100));

  // ── What's already happened today ──
  // Only completed rows get a row. An unticked checkbox on a home screen is a
  // to-do item pointed at a person who may be having a bad day; whatever is
  // outstanding is said in a plain sentence instead, with no box beside it.
  const hasMeals = (mealLog || {})[today]?.filter((e) => e.mealId !== "__water__").length > 0;
  const moved = score.didWorkout || score.didRun;
  const doneItems = [
    ...(moved ? ["Moved"] : []),
    ...(hasMeals ? ["Logged a meal"] : []),
  ];

  const outstandingNote =
    moved && hasMeals
      ? null
      : !moved && !hasMeals
        ? "Nothing logged yet today — whenever you're ready."
        : !moved
          ? "Nothing moved yet today — whenever you're ready."
          : "No meal logged yet today. No rush.";

  // ── One optional suggestion, offered rather than demanded ──
  const nextAction = useMemo(() => {
    const hour = new Date().getHours();
    if (!score.didWorkout && !score.didRun && hour < 21) {
      return { label: "Plan today's movement", action: onStartWorkout };
    }
    const proteinGap = (goals?.protein || 0) - (todayTotals?.protein || 0);
    if (proteinGap > 20 && hour < 20) {
      return { label: `Around ${Math.round(proteinGap)}g of protein left, if you want it`, action: onNavigateToNutrition };
    }
    // No weigh-in prompt here. A body-weight nag has no place on the wellness
    // loop — weight lives on the weight surface, behind a deliberate tap. This
    // card is allowed to simply end.
    return null;
  }, [score, goals, todayTotals, onStartWorkout, onNavigateToNutrition]);

  // ── One plain sentence. Observations, never verdicts. ──
  const narration = useMemo(() => {
    const trained = score.didWorkout || score.didRun;
    const proteinLeft = Math.max(0, (goals?.protein || 0) - protein);

    if (trained && proPct >= 90) return "You moved and you ate well. Good day for you.";
    if (trained && proteinLeft > 0) return "You moved today. Food's part-way there.";
    if (!trained && proPct >= 75) return "You've eaten well today.";
    if (!trained && cals === 0) return "Nothing logged yet. Whenever you're ready.";
    if (!trained) return "Some food logged so far today.";
    return null;
  }, [score, goals, protein, proPct, cals]);

  return (
    <section className="card card--quiet ds-stack" aria-label="Also today">
      <p className="hs-key hs-key--flush">Also today</p>

      {doneItems.length > 0 && (
        <ul className="hs-also-rows">
          {doneItems.map((label) => (
            <li key={label} className="hs-also-row">
              <span className="hs-tick" data-done="true" aria-hidden="true">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span>{label}</span>
            </li>
          ))}
        </ul>
      )}

      {/* One protein line. The calorie ratio-plus-filling-bar is the identical
          MyFitnessPal mechanic whatever it is labelled, so it stays on the
          nutrition tab where a person has chosen to go looking for it. */}
      <MacroBar
        label="Protein"
        current={`${Math.round(protein)}g`}
        goal={`${proGoal}g`}
        pct={proPct}
        color="var(--macro-protein)"
      />

      {outstandingNote ? (
        <p className="ds-support">{outstandingNote}</p>
      ) : (
        narration && <p className="ds-support">{narration}</p>
      )}

      {nextAction && (
        nextAction.action ? (
          <button type="button" className="ds-link" onClick={nextAction.action}>
            {nextAction.label}
          </button>
        ) : (
          <p className="ds-support">{nextAction.label}</p>
        )
      )}
    </section>
  );
}

function MacroBar({ label, current, goal, pct, color }) {
  return (
    <div className="hs-macro">
      <div className="hs-macro-meta">
        <span className="hs-macro-label">{label}</span>
        <span className="hs-macro-value">
          {current} / {goal}
        </span>
      </div>
      <div className="ds-track">
        <div className="ds-track-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
